import { toast } from 'sonner';

// ── Champions card generator ─────────────────────────────────────────────
// Draws a share-ready champions card mirroring the DESKTOP podium panel
// (owner, 2026-07-09: "use the same ratio as the desktop version" — wide
// landscape, NOT square): gold header bar, three columns (2 · 1 · 3), tie
// cards + photo chips, tier steps with drawn medals.
// meta: { eventName, facilityName, date, division, podium: {first, second, third} }
// podium entries: { team_name, players:[names], avatars:[urls], stats, player_stats:[lines] }
// awards (optional, Open Play): [{ label, name, detail }] — the card grows by
// AWARDS_H and draws one tile per award under the podium baseline.

const W = 1600, H = 680;
const BASELINE = H - 80;
const AWARDS_H = 260;

const TIER = {
  1: { color: '#FFD54A', grad: ['#FFD54A', '#B8860B'], ink: '#3A2A00', label: 'CHAMPION', step: 230 },
  2: { color: '#CBD5E1', grad: ['#E2E8F0', '#94A3B8'], ink: '#2A3240', label: 'RUNNER-UP', step: 170 },
  3: { color: '#E0A86B', grad: ['#E0A86B', '#8C5A2B'], ink: '#3A2410', label: '3RD PLACE', step: 135 },
};
const COLS = { 2: { cx: 300, w: 520 }, 1: { cx: 800, w: 400 }, 3: { cx: 1300, w: 480 } };

function ellipsize(ctx, text, maxWidth) {
  let t = String(text || '');
  if (ctx.measureText(t).width <= maxWidth) return t;
  while (ctx.measureText(t + '…').width > maxWidth && t.length > 1) t = t.slice(0, -1);
  return t + '…';
}

function drawMedal(ctx, cx, cy, r, place) {
  const t = TIER[place];
  ctx.save();
  if (place === 1) {
    ctx.shadowColor = 'rgba(255,213,74,0.55)';
    ctx.shadowBlur = 30;
  }
  const g = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
  g.addColorStop(0, t.grad[0]);
  g.addColorStop(1, t.grad[1]);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth = Math.max(2, r * 0.06);
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.78, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = t.ink;
  ctx.font = `900 ${Math.round(r * 1.0)}px Oswald, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(place), cx, cy + r * 0.06);
  ctx.restore();
}

function drawPhoto(ctx, img, cx, cy, r, tierColor, ringW = 4) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  const s = Math.max((2 * r) / img.width, (2 * r) / img.height);
  ctx.drawImage(img, cx - (img.width * s) / 2, cy - (img.height * s) / 2, img.width * s, img.height * s);
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = tierColor;
  ctx.lineWidth = ringW;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function drawInitialCircle(ctx, cx, cy, r, tierColor, name) {
  ctx.save();
  ctx.fillStyle = tierColor + '22';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = tierColor;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = tierColor;
  ctx.font = `900 ${Math.round(r * 1.0)}px Oswald, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText((name || '?')[0].toUpperCase(), cx, cy + 1);
  ctx.restore();
}

function drawCircleFor(ctx, img, cx, cy, r, tier, name, ringW) {
  if (img) drawPhoto(ctx, img, cx, cy, r, tier, ringW);
  else drawInitialCircle(ctx, cx, cy, r, tier, name);
}

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// R2 presigned URLs carry no CORS headers, so a direct crossOrigin load taints.
// Fallback: the same object through our API's public image proxy, which serves
// the bytes WITH CORS — canvas stays clean, photos actually draw.
function proxiedUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    const key = decodeURIComponent(u.pathname.replace(/^\//, ''));
    if (!key.startsWith('sports/site/')) return null;
    return `${API_BASE}/api/sports/public/img/${key.split('/').map(encodeURIComponent).join('/')}`;
  } catch { return null; }
}

function tryLoad(src) {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // required or the canvas taints and export fails
    const timer = setTimeout(() => resolve(null), 4000);
    img.onload = () => { clearTimeout(timer); resolve(img); };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = src;
  });
}

async function loadAvatar(url) {
  if (!url) return null;
  const direct = await tryLoad(url);
  if (direct) return direct;
  const proxy = proxiedUrl(url);
  return proxy ? tryLoad(proxy) : null;
}

const MAX_AVATARS = 12;

// One tie "chip": small photo circle + name, measured for flow-wrap layout
function measureChip(ctx, name, maxW) {
  ctx.font = '600 19px Oswald, sans-serif';
  const label = ellipsize(ctx, name, maxW);
  return { label, w: 22 + 7 + ctx.measureText(label).width };
}

function drawColumn(ctx, place, entry, imgs) {
  if (!entry) return;
  const t = TIER[place];
  const { cx, w: colW } = COLS[place];
  const stepTop = BASELINE - t.step;

  // ── Step block (medal + label + tie tag inside, like the display)
  const stepW = Math.round(colW * 0.62);
  const grad = ctx.createLinearGradient(0, stepTop, 0, BASELINE);
  grad.addColorStop(0, t.color + '26');
  grad.addColorStop(1, t.color + '0A');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.roundRect(cx - stepW / 2, stepTop, stepW, t.step, [16, 16, 0, 0]);
  ctx.fill();
  ctx.strokeStyle = t.color + '55';
  ctx.lineWidth = 2;
  ctx.stroke();

  const players = entry.players || [];
  const isTie = players.length > 1 && players.join(' · ') === entry.team_name;

  ctx.save();
  ctx.textAlign = 'center';
  const medalR = place === 1 ? 40 : 30;
  drawMedal(ctx, cx, stepTop + 24 + medalR, medalR, place);
  ctx.font = '900 21px Oswald, sans-serif';
  ctx.fillStyle = t.color;
  let sy = stepTop + 24 + medalR * 2 + 32;
  ctx.fillText(t.label, cx, sy);
  if (isTie) {
    ctx.font = '700 16px Oswald, sans-serif';
    ctx.globalAlpha = 0.75;
    ctx.fillText(`${players.length}-WAY TIE`, cx, sy + 24);
    ctx.globalAlpha = 1;
  }
  ctx.restore();

  const blockBottom = stepTop - 20;
  ctx.textAlign = 'center';

  if (!isTie) {
    // ── Solo winner / named team: big photo, name, record
    const name = entry.team_name;
    const photoR = place === 1 ? 54 : 44;
    const nameH = place === 1 ? 40 : 32;
    const statsH = entry.stats ? 28 : 0;
    const totalH = photoR * 2 + 14 + nameH + statsH;
    const top = blockBottom - totalH;
    drawCircleFor(ctx, imgs && imgs[0], cx, top + photoR, photoR, t.color, name, 5);
    if (imgs && imgs[0]) drawMedal(ctx, cx + photoR * 0.72, top + photoR + photoR * 0.72, medalR * 0.55, place);
    ctx.font = `900 ${place === 1 ? 32 : 26}px Oswald, sans-serif`;
    ctx.fillStyle = '#FFFFFF';
    let y = top + photoR * 2 + 14 + (place === 1 ? 30 : 24);
    ctx.fillText(ellipsize(ctx, name.toUpperCase(), colW - 40), cx, y);
    if (entry.stats) {
      ctx.font = '700 19px Oswald, sans-serif';
      ctx.fillStyle = t.color;
      ctx.fillText(entry.stats, cx, y + 26);
    }
    // roster (real teams): players under the headline
    if (players.length > 1) {
      ctx.font = '400 18px sans-serif';
      ctx.fillStyle = '#A3A3A3';
      ctx.fillText(ellipsize(ctx, players.join(' · '), colW - 40), cx, y + (entry.stats ? 50 : 26));
    }
    return;
  }

  // ── Tie: top-3 as mini cards (photo, name, record) + everyone else as
  // photo chips flowing in centered rows — the display's exact structure
  const cards = players.slice(0, 3);
  const rest = players.slice(3);

  // chips flow layout (measure first)
  const rows = [];
  {
    let row = [], rowW = 0;
    for (let i = 0; i < rest.length; i++) {
      const chip = measureChip(ctx, rest[i], 170);
      chip.idx = i + 3;
      if (rowW + chip.w + (row.length ? 18 : 0) > colW - 24 && row.length) {
        rows.push(row); row = []; rowW = 0;
      }
      rowW += chip.w + (row.length ? 18 : 0);
      row.push(chip);
    }
    if (row.length) rows.push(row);
  }
  const chipRowH = 32;
  const cardH = 60 + 10 + 24 + 20; // photo + gap + name + stats
  const totalH = cardH + (rows.length ? 12 + rows.length * chipRowH : 0);
  const top = blockBottom - totalH;

  // cards row
  const spacing = Math.min(180, (colW - 60) / Math.max(cards.length - 1, 1));
  const startX = cx - spacing * (cards.length - 1) / 2;
  cards.forEach((name, i) => {
    const ccx = startX + i * spacing;
    drawCircleFor(ctx, imgs && imgs[i], ccx, top + 30, 30, t.color, name, 3);
    ctx.font = '800 19px Oswald, sans-serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(ellipsize(ctx, name.toUpperCase(), spacing - 14), ccx, top + 60 + 10 + 16);
    const st = entry.player_stats && entry.player_stats[i];
    if (st) {
      ctx.font = '700 13px Oswald, sans-serif';
      ctx.fillStyle = t.color;
      ctx.fillText(ellipsize(ctx, st, spacing - 8), ccx, top + 60 + 10 + 34);
    }
  });

  // chip rows (centered flow)
  let cy = top + cardH + 12;
  for (const row of rows) {
    const rowW = row.reduce((n, c) => n + c.w, 0) + 18 * (row.length - 1);
    let x = cx - rowW / 2;
    for (const chip of row) {
      drawCircleFor(ctx, imgs && imgs[chip.idx], x + 11, cy + chipRowH / 2 - 4, 11, t.color, players[chip.idx], 2);
      ctx.save();
      ctx.textAlign = 'left';
      ctx.font = '600 19px Oswald, sans-serif';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText(chip.label, x + 29, cy + chipRowH / 2 + 3);
      ctx.restore();
      x += chip.w + 18;
    }
    cy += chipRowH;
  }
}

function drawAwards(ctx, awards) {
  const list = awards.slice(0, 3);
  const x0 = 48, x1 = W - 48, gap = 24;
  const w = (x1 - x0 - gap * (list.length - 1)) / list.length;
  const top = BASELINE + 58, bottom = BASELINE + AWARDS_H - 22;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.fillStyle = '#D6C08A';
  ctx.font = '700 20px Oswald, sans-serif';
  if ('letterSpacing' in ctx) ctx.letterSpacing = '5px';
  ctx.fillText('AWARDS', W / 2, BASELINE + 40);
  if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
  list.forEach((a, i) => {
    const l = x0 + i * (w + gap), cx = l + w / 2;
    ctx.fillStyle = 'rgba(255,213,74,0.06)';
    ctx.strokeStyle = 'rgba(255,213,74,0.28)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(l, top, w, bottom - top, 18);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#D6C08A';
    ctx.font = '700 18px Oswald, sans-serif';
    ctx.fillText(String(a.label || '').toUpperCase(), cx, top + 40);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '900 38px Oswald, sans-serif';
    ctx.fillText(ellipsize(ctx, String(a.name || '').toUpperCase(), w - 40), cx, top + 92);
    ctx.fillStyle = '#A3A3A3';
    ctx.font = '400 19px sans-serif';
    ctx.fillText(ellipsize(ctx, a.detail || '', w - 40), cx, top + 130);
  });
  ctx.restore();
}

export async function buildChampionsCanvas({ eventName, facilityName, date, division, podium, awards }, { noPhotos = false } = {}) {
  const hasAwards = Array.isArray(awards) && awards.length > 0;
  const H2 = hasAwards ? H + AWARDS_H : H;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H2;
  const ctx = canvas.getContext('2d');

  // Every podium player's photo (ties included), best-effort in parallel
  const loadEntry = (e) => !e || noPhotos
    ? Promise.resolve([])
    : Promise.all((e.avatars || []).slice(0, MAX_AVATARS).map(loadAvatar));
  const [imgs1, imgs2, imgs3] = await Promise.all([
    loadEntry(podium?.first), loadEntry(podium?.second), loadEntry(podium?.third),
  ]);

  // Background — the podium panel's gradient + gold frame
  const bg = ctx.createLinearGradient(0, 0, 0, H2);
  bg.addColorStop(0, '#1A1408');
  bg.addColorStop(0.6, '#0D0D0D');
  bg.addColorStop(1, '#0A0A0A');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H2);
  const glow = ctx.createRadialGradient(W / 2, 0, 60, W / 2, 0, 640);
  glow.addColorStop(0, 'rgba(255,213,74,0.10)');
  glow.addColorStop(1, 'rgba(255,213,74,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(255,213,74,0.25)';
  ctx.lineWidth = 3;
  ctx.strokeRect(16, 16, W - 32, H2 - 32);

  // Header bar (like the display panel header)
  ctx.fillStyle = 'rgba(255,213,74,0.06)';
  ctx.fillRect(16, 16, W - 32, 74);
  ctx.strokeStyle = 'rgba(255,213,74,0.18)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(16, 90); ctx.lineTo(W - 16, 90); ctx.stroke();

  ctx.save();
  ctx.textAlign = 'left';
  ctx.fillStyle = '#FFD54A';
  ctx.font = '900 30px Oswald, sans-serif';
  if ('letterSpacing' in ctx) ctx.letterSpacing = '4px';
  ctx.fillText(division ? `${String(division).toUpperCase()} — CHAMPIONS` : 'CHAMPIONS', 56, 65);
  ctx.restore();

  ctx.save();
  ctx.textAlign = 'right';
  ctx.fillStyle = '#A3A3A3';
  ctx.font = '600 21px Oswald, sans-serif';
  const meta = [facilityName, eventName].filter(Boolean).join('  ·  ').toUpperCase();
  ctx.fillText(ellipsize(ctx, meta, W / 2 + 200), W - 56, 63);
  ctx.restore();

  // Columns — 2 · 1 · 3, like the desktop podium
  drawColumn(ctx, 2, podium?.second, imgs2);
  drawColumn(ctx, 1, podium?.first, imgs1);
  drawColumn(ctx, 3, podium?.third, imgs3);

  // Baseline + footer
  ctx.strokeStyle = '#FFD54A55';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(48, BASELINE); ctx.lineTo(W - 48, BASELINE); ctx.stroke();
  if (hasAwards) drawAwards(ctx, awards);
  ctx.textAlign = 'center';
  ctx.fillStyle = '#737373';
  ctx.font = '400 20px sans-serif';
  const footer = [date, 'Powered by AgriBooks Sports'].filter(Boolean).join('  ·  ');
  ctx.fillText(footer, W / 2, H2 - 34);

  return canvas;
}

function cardFilename(meta) {
  const base = `${meta.eventName || 'champions'}${meta.division ? '-' + meta.division : ''}`;
  return `champions-${base.replace(/[^a-z0-9-]+/gi, '-').toLowerCase()}.png`;
}

// If a photo host lacks CORS headers the canvas taints and export throws —
// rebuild medal-only rather than failing the share/download.
async function exportBlob(meta) {
  let canvas = await buildChampionsCanvas(meta);
  try {
    const blob = await new Promise((res, rej) => canvas.toBlob(b => (b ? res(b) : rej(new Error('taint'))), 'image/png'));
    return blob;
  } catch {
    canvas = await buildChampionsCanvas(meta, { noPhotos: true });
    return new Promise(res => canvas.toBlob(res, 'image/png'));
  }
}

export async function downloadChampionsCard(meta) {
  const blob = await exportBlob(meta);
  const a = document.createElement('a');
  a.download = cardFilename(meta);
  a.href = URL.createObjectURL(blob);
  a.click();
  URL.revokeObjectURL(a.href);
  toast.success('Champions card downloaded');
}

export async function shareChampionsCard(meta) {
  const blob = await exportBlob(meta);
  const file = new File([blob], cardFilename(meta), { type: 'image/png' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: `${meta.eventName} — Champions`,
        text: `🏆 ${meta.podium?.first?.team_name || ''} wins ${meta.eventName}${meta.facilityName ? ` at ${meta.facilityName}` : ''}!`,
      });
      return;
    } catch (e) {
      if (e.name === 'AbortError') return; // user cancelled
    }
  }
  // Fallback: download
  const a = document.createElement('a');
  a.download = cardFilename(meta);
  a.href = URL.createObjectURL(blob);
  a.click();
  URL.revokeObjectURL(a.href);
  toast.success('Sharing not supported here — card downloaded instead');
}
